// Session lib for I24 — talks to the D1-native /auth/d1 endpoints.
//
// Cookie-based: the Worker writes `ik_session` (HttpOnly, SameSite=None,
// Partitioned). We never read the token from JS; presence is inferred from
// `/me` succeeding. `currentUser` is cached in a module-level signal so any
// component can `import { user } from "./auth"`.

import { createSignal } from "solid-js";
import { apiBase } from "./api";
import { reconcileVotes } from "./likes";

export type User = {
  id: string;
  username: string;
  display_name: string | null;
  email: string | null;
  bio: string | null;
  avatar_url: string | null;
};

const [user, setUser] = createSignal<User | null>(null);
const [loaded, setLoaded] = createSignal(false);
export { user, loaded };

const API = apiBase;

function fetchOpts(extra: RequestInit = {}): RequestInit {
  return {
    credentials: "include",
    ...extra,
    headers: {
      "content-type": "application/json",
      ...(extra.headers ?? {}),
    },
  };
}

export async function refreshMe(): Promise<User | null> {
  try {
    const res = await fetch(`${API}/me`, fetchOpts({ method: "GET" }));
    if (!res.ok) {
      setUser(null);
      return null;
    }
    const body = (await res.json()) as { user: User | null };
    setUser(body.user ?? null);
    if (body.user) {
      // WEB62: already-logged-in session with a pending anon mirror —
      // converge it (once per session, no-op when empty).
      void reconcileVotes();
    }
    return body.user ?? null;
  } catch {
    setUser(null);
    return null;
  } finally {
    setLoaded(true);
  }
}

export async function signup(input: {
  username: string;
  password: string;
  display_name?: string;
  email?: string;
}): Promise<User> {
  const res = await fetch(
    `${API}/auth/d1/signup`,
    fetchOpts({
      method: "POST",
      body: JSON.stringify({
        username: input.username.trim().toLowerCase(),
        password: input.password,
        display_name: input.display_name?.trim() || undefined,
        email: input.email?.trim() || undefined,
      }),
    }),
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `signup failed (${res.status})`);
  }
  const body = (await res.json()) as { user: User };
  setUser(body.user);
  setLoaded(true);
  // Attempt to claim any anon pieces the cookie still references. Best
  // effort — a 200 with `claimed: 0` is fine.
  try {
    await claimAnon();
  } catch {
    /* non-fatal */
  }
  // WEB62: converge the anon vote mirror onto the new account.
  try {
    await reconcileVotes();
  } catch {
    /* non-fatal */
  }
  return body.user;
}

export async function login(input: {
  username: string;
  password: string;
}): Promise<User> {
  const res = await fetch(
    `${API}/auth/d1/login`,
    fetchOpts({
      method: "POST",
      body: JSON.stringify({
        username: input.username.trim().toLowerCase(),
        password: input.password,
      }),
    }),
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `login failed (${res.status})`);
  }
  const body = (await res.json()) as { user: User };
  setUser(body.user);
  setLoaded(true);
  try {
    await claimAnon();
  } catch {
    /* non-fatal */
  }
  // WEB62: converge the anon vote mirror onto the account.
  try {
    await reconcileVotes();
  } catch {
    /* non-fatal */
  }
  return body.user;
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API}/auth/d1/logout`, fetchOpts({ method: "POST" }));
  } finally {
    setUser(null);
  }
}

export type ClaimResult = {
  claimed: number;
  anon_uid: string | null;
  user_id?: string;
  replayed?: boolean;
  /** API63: votes re-keyed anon → user by this claim (0 on clean replay). */
  claimed_votes?: number;
  /** API63: anon duplicates dropped (authed row won). */
  dropped_votes?: number;
};

export async function claimAnon(): Promise<ClaimResult> {
  const res = await fetch(
    `${API}/me/claim-anon`,
    fetchOpts({ method: "POST" }),
  );
  if (!res.ok) {
    throw new Error(`claim failed (${res.status})`);
  }
  return (await res.json()) as ClaimResult;
}

export type MyPiece = {
  id: string;
  image_url: string;
  prompt: string | null;
  likes_count: number | null;
  dislikes_count: number | null;
  core_rank: number | null;
  oort_rank: number | null;
  display_zone: string | null;
  created_at: number;
  status: string | null;
};

export async function updateProfile(input: {
  display_name: string;
  bio?: string;
  avatar_url?: string;
}): Promise<User> {
  const res = await fetch(
    `${API}/me/profile`,
    fetchOpts({
      method: "POST",
      body: JSON.stringify({
        display_name: input.display_name,
        bio: input.bio ?? "",
        avatar_url: input.avatar_url ?? "",
      }),
    }),
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `profile update failed (${res.status})`);
  }
  // The endpoint echoes email as null (column untouched) — re-fetch the
  // full user so the cache never holds a partial row.
  const me = await refreshMe();
  if (!me) throw new Error("unauthorized");
  return me;
}

export async function fetchMyPieces(): Promise<MyPiece[]> {
  const res = await fetch(`${API}/me/pieces`, fetchOpts({ method: "GET" }));
  if (!res.ok) return [];
  const body = (await res.json()) as { pieces: MyPiece[] };
  return body.pieces ?? [];
}

// --- Legacy curator gate (kept for CuratorInbox/CuratorCanvas/CuratorAudit) ---

const DEV_ADMIN =
  (import.meta.env.VITE_DEV_ADMIN ?? "1").toString().trim() !== "0";

export function isAdmin(): boolean {
  if (DEV_ADMIN) return true;
  try {
    return globalThis.localStorage?.getItem("ikamiro.admin") === "1";
  } catch {
    return false;
  }
}

/** Legacy demo helper. Prefer `user()?.id` when an account exists. */
export function currentArtistId(): string {
  const u = user();
  if (u) return u.id;
  try {
    return globalThis.localStorage?.getItem("ikamiro.artist_id") ?? "a_demo";
  } catch {
    return "a_demo";
  }
}
